/**
 * Products Page with Server-Side Search
 *
 * READ-ONLY view of all products with server-side pagination, sorting, and filtering.
 * Products can only be edited within their associated deals, not directly here.
 *
 * Uses in-memory search index for fast pagination of large datasets (~10k+ items).
 */

import { Alert, Box, Button } from '@mui/material'
import {
  GridColDef,
  GridDensity,
  GridFilterModel,
  GridRowSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium'
import { checkProductsSearchHealth, useServerSideProducts } from 'api/search'
import { gridContainerStyles } from 'components/Contacts/style'
import GlobalDataGrid from 'components/DataGrid/GlobalDataGrid'
import GlobalGridToolbar from 'components/DataGrid/GlobalGridToolbar'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import dayjs from 'dayjs'
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useProducts } from 'api/aws/useProducts'
import { loadModel } from 'components/DataGrid/operators/loadModels'
import { OrganizationTableItem } from 'types'
import { defaultColumnOrder, defaultFilterModel, defaultSortModel, defaultVisibilityModel } from './defaultModels'
import ProductInfoDialog from './ProductInfoDialog'
import ProductsGridFooter from './ProductsGridFooter'
import { renderProductColumns } from './renderProductColumns'

const CONTEXT = 'products'

const ProductsPageServerSide = () => {
  const navigate = useNavigate()
  const { pinned } = useLayoutContext()
  const apiRef = useGridApiRef()

  // UI State
  const [selectedItem, setSelectedItem] = useState<any>(null)
  const [infoOpen, setInfoOpen] = useState(false)
  const [density, setDensity] = useState<GridDensity>(() => {
    const saved = localStorage.getItem('productsDensityModel') as GridDensity | null
    return saved ?? 'standard'
  })
  const { data: gro_products } = useProducts(true)
  // Month filter — filters products whose startDate–endDate range overlaps the selected month
  const MONTH_PREFIX = 'month-range-'
  const [monthFilter, setMonthFilter] = useState(() => {
    const saved = localStorage.getItem('productsMonthFilter')
    return saved !== null ? saved : dayjs().format('YYYY-MM')
  })

  // Persist month filter selection
  useEffect(() => {
    localStorage.setItem('productsMonthFilter', monthFilter)
  }, [monthFilter])

  const monthFilterItems = useMemo(() => {
    if (!monthFilter) return []
    const startOfMonth = dayjs(monthFilter).startOf('month').format('YYYY-MM-DD')
    const endOfMonth = dayjs(monthFilter).endOf('month').format('YYYY-MM-DD')
    return [
      { id: `${MONTH_PREFIX}start`, field: 'startDate', operator: 'onOrBefore', value: endOfMonth },
      { id: `${MONTH_PREFIX}end`, field: 'endDate', operator: 'onOrAfter', value: startOfMonth },
    ]
  }, [monthFilter])

  // Load persisted column filters from localStorage and merge with month items
  // so the hook's initial API call includes any filters the user had active last session
  const initialFilterModel = useMemo(() => {
    const persisted = loadModel<GridFilterModel>(CONTEXT, 'FilterModel', defaultFilterModel)
    const userItems = (persisted.items || []).filter(item => !item.id?.toString().startsWith(MONTH_PREFIX))
    return {
      ...persisted,
      items: [...userItems, ...monthFilterItems],
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only compute once on mount

  // Server-side search state
  const [searchAvailable, setSearchAvailable] = useState<boolean | null>(null)
  const [syncStatus, setSyncStatus] = useState<{
    documentCount?: number
    lastSyncTime?: string
    isInitialized?: boolean
  }>({})
  const handleRowClick = (row: OrganizationTableItem) => {
    const route =
      row?.EntityType === 'deal'
        ? `/deals/${row.PK}/DEAL_${row?._resolvedDeal?.deal_uuid}/details`
        : `/organizations/${row.PK}/details`
    navigate(route)
  }
  // Check if search is available on mount
  useEffect(() => {
    const checkHealth = async () => {
      const result = await checkProductsSearchHealth()
      setSearchAvailable(result.available)
      setSyncStatus({
        documentCount: result.documentCount,
        lastSyncTime: result.lastSyncTime,
        isInitialized: result.isInitialized,
      })
      if (!result.available) {
        console.warn('[ProductsPageServerSide] Search not available:', result.message)
      }
    }

    checkHealth()
    const interval = setInterval(checkHealth, 30000)
    return () => clearInterval(interval)
  }, [])

  // Use server-side hook — seed initial filter with current month range
  const {
    rows: serverRows,
    aggregations,
    totalRowCount,
    isLoading: isSearchLoading,
    error: searchError,
    sortModel,
    onSortModelChange,
    filterModel,
    onFilterModelChange,
    serverSideProps,
    refresh,
  } = useServerSideProducts({
    enabled: searchAvailable === true,
    pageSize: 100,
    initialSortModel: defaultSortModel,
    initialFilterModel,
  })
  const isLoading = searchAvailable === null || searchAvailable === false || isSearchLoading

  // Wrap onFilterModelChange to always re-inject month filter items
  const wrappedFilterModelChange = useCallback(
    (newModel: GridFilterModel) => {
      const userItems = newModel.items.filter(item => !item.id?.toString().startsWith(MONTH_PREFIX))
      onFilterModelChange({
        ...newModel,
        items: [...userItems, ...monthFilterItems],
      })
    },
    [onFilterModelChange, monthFilterItems]
  )

  // When month changes, re-apply to current filter model
  useEffect(() => {
    const userItems = filterModel.items.filter(item => !item.id?.toString().startsWith(MONTH_PREFIX))
    const updatedItems = [...userItems, ...monthFilterItems]
    console.log('[MonthFilter] Applying filter:', JSON.stringify(updatedItems, null, 2))
    onFilterModelChange({
      ...filterModel,
      items: updatedItems,
    })
  }, [monthFilterItems])

  // Clear filters callback for footer
  const onClearFilters = useCallback(() => {
    onFilterModelChange({ items: [...monthFilterItems], quickFilterValues: [] })
  }, [onFilterModelChange, monthFilterItems])

  // Open product info dialog
  const openInfo = useCallback((product: any) => {
    setSelectedItem(product)
    setInfoOpen(true)
  }, [])

  const closeInfo = useCallback(() => {
    setInfoOpen(false)
    setSelectedItem(null)
  }, [])

  // Process rows (just add rowId)
  const processedRows = useMemo(() => {
    if (!serverRows) return []

    return serverRows.map(product => ({
      ...product,
      rowId: product.SK || product.products_uuid || `${product.PK}:${product.SK}`,
      id: product.products_uuid || product.SK,
    }))
  }, [serverRows])

  const changeDensity = useCallback((newDensity: GridDensity) => {
    setDensity(newDensity)
    localStorage.setItem('productsDensityModel', newDensity)
  }, [])

  // Define columns using denormalized data from server
  const columns: GridColDef[] = renderProductColumns(density, openInfo, handleRowClick)

  const defaultColumnWidths = useMemo(
    () => Object.fromEntries(columns.filter(col => typeof col.width === 'number').map(col => [col.field, col.width])),
    [columns]
  )

  const FooterWrapper = useCallback(
    ({
      rowSelectionModel,
      setRowSelectionModel,
    }: {
      rowSelectionModel: GridRowSelectionModel
      setRowSelectionModel: Dispatch<SetStateAction<GridRowSelectionModel>>
    }) => <ProductsGridFooter serverSideMode={true} />,
    []
  )

  const gridContainerSx = useMemo(
    () => ({
      position: 'relative',
      ...gridContainerStyles,
      pt: pinned ? '60px' : 0.8,
      pb: 2,
      transition: 'all 0.3s ease-in-out',
      height: '100%',
    }),
    [pinned]
  )

  // Show error if search failed
  if (searchError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Search error: {searchError}
        </Alert>
        <Button variant="outlined" onClick={() => refresh()}>
          Retry
        </Button>
      </Box>
    )
  }

  return (
    <>
      <GlobalDataGrid
        rows={processedRows}
        columns={columns}
        apiRef={apiRef}
        context={CONTEXT}
        defaultFilterModel={defaultFilterModel}
        defaultVisibilityModel={defaultVisibilityModel}
        defaultSortModel={defaultSortModel}
        defaultGridDensity="standard"
        defaultColumnWidths={defaultColumnWidths}
        defaultColumnOrder={defaultColumnOrder}
        aggregationModel={{
          price: 'sum',
          netTotal: 'sum',
          mediaSpend: 'sum',
        }}
        toolbarComponent={GlobalGridToolbar}
        FooterComponent={FooterWrapper}
        onChangeDensity={changeDensity}
        gridContainerSx={gridContainerSx}
        treeData={false}
        isLoading={isLoading}
        checkboxSelection={false}
        // Server-side mode props
        serverSideMode={true}
        rowCount={totalRowCount}
        paginationModel={serverSideProps.paginationModel}
        onPaginationModelChange={serverSideProps.onPaginationModelChange}
        onSortModelChange={onSortModelChange}
        onFilterModelChange={wrappedFilterModelChange}
        onClearFilters={onClearFilters}
        onChangeMonth={setMonthFilter}
        monthFilter={monthFilter}
        syncStatus={syncStatus}
      />
      {infoOpen && <ProductInfoDialog open={infoOpen} item={selectedItem} onClose={closeInfo} />}
    </>
  )
}

export default ProductsPageServerSide
